import {
  UseCeloConfig,
  ApiResponse,
  User,
  Subscription,
  Feature,
  Plan,
  FeatureAccessResult,
  IdentifyResponse,
  PlanChangeResponse,
  CancelResponse,
  CacheManager,
  CacheEntry,
  ApiError,
  AuthError,
  NetworkError
} from '../types';

// Simple in-memory cache implementation
class MemoryCache implements CacheManager {
  private cache = new Map<string, CacheEntry<any>>();

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() > entry.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  set<T>(key: string, data: T, ttl: number = 300000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + ttl
    });
  }

  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  isValid(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    return Date.now() <= entry.expiry;
  }
}

export class UseCeloApiClient {
  private config: UseCeloConfig;
  private cache: CacheManager;
  private authToken: string | null = null;

  constructor(config: UseCeloConfig) {
    this.config = {
      apiHost: 'https://usecelo.com',
      enableCache: true,
      cacheTTL: 300000, // 5 minutes
      ...config
    };
    
    this.cache = new MemoryCache();
  }

  // Update configuration
  updateConfig(config: Partial<UseCeloConfig>): void {
    this.config = { ...this.config, ...config };
    if (config.userId) {
      // Clear cache when user changes
      this.cache.clear();
      this.authToken = null;
    }
  }

  // Set authentication token
  setAuthToken(token: string): void {
    this.authToken = token;
  }

  // Generic API request method
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    useCache: boolean = true,
    cacheTTL?: number
  ): Promise<T> {
    const cacheKey = `${endpoint}-${JSON.stringify(options.body || {})}`;
    
    // Check cache first
    if (useCache && this.config.enableCache && options.method !== 'POST') {
      const cached = this.cache.get<T>(cacheKey);
      if (cached) return Promise.resolve(cached as T);
    }

    const url = `${this.config.apiHost}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-api-key': this.config.apiKey || '',
      ...((options.headers as Record<string, string>) || {})
    };

    // Add auth token if available
    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.authToken = null; // Clear invalid token
          throw new AuthError('Invalid API key or authentication token');
        }
        
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.error || `HTTP ${response.status}`,
          response.status.toString(),
          errorData
        );
      }

      const data: ApiResponse<T> = await response.json();
      
      if (!data.success) {
        throw new ApiError(data.error || 'API request failed', '400', 400);
      }

      const result = data.data!;

      // Cache successful responses
      if (useCache && this.config.enableCache && options.method !== 'POST') {
        this.cache.set(cacheKey, result, cacheTTL || this.config.cacheTTL);
      }

      return result;
    } catch (error) {
      if (error instanceof ApiError || error instanceof AuthError) {
        throw error;
      }
      
      // Network or other errors
      throw new NetworkError(`Request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // User Management APIs
  async identify(userData: {
    externalId: string;
    email?: string;
    name?: string;
    metadata?: Record<string, any>;
  }): Promise<IdentifyResponse> {
    const response = await this.request<IdentifyResponse>(
      '/api/public/users/identify',
      {
        method: 'POST',
        body: JSON.stringify(userData)
      },
      false // Don't cache identify requests
    );

    // Note: Token handling is done at app level in this version
    return response;
  }

  async syncUser(userData: {
    externalId: string;
    email: string;
    name?: string;
    metadata?: Record<string, any>;
    teamId?: string;
    role?: string;
  }): Promise<{ user: User; subscription: Subscription | null }> {
    return this.request(
      '/api/public/users/sync',
      {
        method: 'POST',
        body: JSON.stringify(userData)
      },
      false
    );
  }

  async getUserDetails(externalId: string): Promise<{
    user: User;
    subscription: Subscription | null;
    usage: Record<string, number>;
  }> {
    return this.request(
      `/api/public/users/sync?externalId=${encodeURIComponent(externalId)}`,
      {},
      true,
      60000 // Cache for 1 minute
    );
  }

  // Feature Access APIs
  async checkFeature(featureName: string, userId?: string): Promise<FeatureAccessResult> {
    const userParam = userId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required for feature checking');
    }

    return this.request(
      `/api/public/entitlements/${this.config.appId}?userId=${encodeURIComponent(userParam as string)}&featureName=${encodeURIComponent(featureName)}`,
      {},
      true,
      30000 // Cache for 30 seconds
    );
  }

  async getFeatures(planId?: string): Promise<{ features: Feature[]; totalFeatures: number }> {
    const endpoint = planId 
      ? `/api/public/features/${this.config.appId}?planId=${planId}`
      : `/api/public/features/${this.config.appId}`;
    
    return this.request(endpoint, {}, true, this.config.cacheTTL);
  }

  // Usage Tracking APIs
  async trackUsage(unitType: string, amount: number = 1, userId?: string): Promise<void> {
    const userParam = userId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required for usage tracking');
    }

    await this.request(
      '/api/public/usage/increment',
      {
        method: 'POST',
        body: JSON.stringify({
          userId: userParam,
          unitType,
          amount
        })
      },
      false // Never cache usage tracking
    );

    // Invalidate related cache entries
    this.cache.delete(`/api/public/entitlements/${this.config.appId}?userId=${encodeURIComponent(userParam as string)}&featureName=${unitType}`);
    this.cache.delete(`/api/public/subscriptions/current?externalId=${encodeURIComponent(userParam as string)}`);
  }

  // Subscription APIs
  async getCurrentSubscription(externalId?: string): Promise<{
    user: User;
    subscription: Subscription | null;
  }> {
    const userParam = externalId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required for subscription data');
    }

    return this.request(
      `/api/public/subscriptions/current?externalId=${encodeURIComponent(userParam as string)}`,
      {},
      true,
      60000 // Cache for 1 minute
    );
  }

  async changePlan(
    newPlanId: string,
    immediate: boolean = false,
    externalId?: string
  ): Promise<PlanChangeResponse> {
    const userParam = externalId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required for plan changes');
    }

    const response = await this.request<PlanChangeResponse>(
      '/api/public/subscriptions/change-plan',
      {
        method: 'POST',
        body: JSON.stringify({
          externalId: userParam,
          newPlanId,
          immediate
        })
      },
      false
    );

    // Invalidate subscription cache
    this.cache.delete(`/api/public/subscriptions/current?externalId=${encodeURIComponent(userParam as string)}`);
    
    return response;
  }

  async cancelSubscription(
    immediate: boolean = false,
    reason?: string,
    feedback?: string,
    externalId?: string
  ): Promise<CancelResponse> {
    const userParam = externalId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required for subscription cancellation');
    }

    const response = await this.request<CancelResponse>(
      '/api/public/subscriptions/cancel',
      {
        method: 'POST',
        body: JSON.stringify({
          externalId: userParam,
          immediate,
          reason,
          feedback
        })
      },
      false
    );

    // Invalidate subscription cache
    this.cache.delete(`/api/public/subscriptions/current?externalId=${encodeURIComponent(userParam as string)}`);
    
    return response;
  }

  // Plan APIs
  async getPlans(): Promise<Plan[]> {
    const response = await this.request<Plan[]>(
      `/api/public/plans/${this.config.appId}`,
      {},
      true,
      this.config.cacheTTL
    );
    return response;
  }

  async getPlan(planId: string): Promise<Plan> {
    return this.request(
      `/api/public/plans/${this.config.appId}/${planId}`,
      {},
      true,
      this.config.cacheTTL
    );
  }

  // Trial APIs
  async startTrial(planId: string, userId?: string): Promise<{ message: string }> {
    const userParam = userId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required to start trial');
    }

    const response = await this.request<{ message: string }>(
      '/api/public/trials/start',
      {
        method: 'POST',
        body: JSON.stringify({
          planId,
          userId: userParam
        })
      },
      false
    );

    // Invalidate subscription cache
    this.cache.delete(`/api/public/subscriptions/current?externalId=${encodeURIComponent(userParam as string)}`);
    
    return response;
  }

  // Cache Management
  clearCache(): void {
    this.cache.clear();
  }

  invalidateCache(pattern?: string): void {
    if (pattern) {
      // This is a simple implementation - could be enhanced with regex patterns
      this.cache.clear();
    } else {
      this.cache.clear();
    }
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.request('/api/public/health', { method: 'GET' }, false);
      return true;
    } catch {
      return false;
    }
  }
}